import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, Space, Typography, notification } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import PageWrapper from '../../../ui/PageWrapper';
import { InternshipService } from '../../../services/InternshipService';
import { DictionaryCatalogDtoResponse } from '../../../interfaces/DictionaryCatalogDtoResponse';


const InternshipCategories = () => {
    const [data, setData] = useState<DictionaryCatalogDtoResponse[]>([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingItem, setEditingItem] = useState<DictionaryCatalogDtoResponse | null>(null);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchInternshipCategories();
    }, []);

    const fetchInternshipCategories = async () => {
        setLoading(true);
        try {
            const response = await InternshipService.getCategories();
            setData(response.data);
        } catch (error) {
            notification.error({ message: "Error fetching data" });
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        setEditingItem(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEdit = (record: DictionaryCatalogDtoResponse) => {
        setEditingItem(record);
        form.setFieldsValue(record);
        setIsModalVisible(true);
    };

    const handleDelete = (id: number) => {
        Modal.confirm({
            title: 'Are you sure you want to delete this item?',
            onOk: async () => {
                setLoading(true);
                try {
                    await InternshipService.deleteCategory(id);
                    notification.success({ message: "Internship category deleted" });
                    fetchInternshipCategories();
                } catch (error) {
                    notification.error({ message: "Error deleting internship category" });
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            if (editingItem) {
                await InternshipService.updateCategory(editingItem.id, values);
                notification.success({ message: "Internship category successfully updated" });
            } else {
                await InternshipService.createCategory(values);
                notification.success({ message: "Internship category successfully created" });
            }

            setIsModalVisible(false);
            fetchInternshipCategories();
        } catch (error) {
            notification.error({ message: "Error saving internship category" });
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        { title: 'Name (KZ)', dataIndex: 'name', key: 'name' },
        { title: 'Name (RU)', dataIndex: 'nameRu', key: 'nameRu' },
        { title: 'Name (EN)', dataIndex: 'nameEn', key: 'nameEn' },
        {
            title: 'Actions',
            key: 'actions',
            render: (record: DictionaryCatalogDtoResponse) => (
                <Space>
                    <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                    <Button icon={<DeleteOutlined />} danger onClick={() => handleDelete(record.id)} />
                </Space>
            ),
        },
    ];

    return (
        <PageWrapper>
            <Typography.Title level={1}>Internship Categories</Typography.Title>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} style={{ marginBottom: 16 }}>
                Add Internship Category
            </Button>
            <Table columns={columns} dataSource={data} loading={loading} rowKey="id" />
            <Modal
                title={editingItem ? 'Edit Internship Category' : 'Add Internship Category'}
                visible={isModalVisible}
                onOk={handleSave}
                onCancel={() => setIsModalVisible(false)}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label="Name (KZ)" >
                        <Input />
                    </Form.Item>
                    <Form.Item name="nameRu" label="Name (RU)" >
                        <Input />
                    </Form.Item>
                    <Form.Item name="nameEn" label="Name (EN)" >
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>
        </PageWrapper>
    );
};

export default InternshipCategories;

